import { FC, ChangeEvent, useEffect, useState, RefObject } from 'react';
import { useUserSetOptimizelyOverrides } from '../hooks/useUserSetOptimizelyOverrides';
import {
    useLazyOptimizelyExperiment,
    useLazyOptimizelyFeatureFlag
} from '../hooks/useDecide';
import { IconButton } from '@zoopla-eng/zds-components/button';

import { OptimizelyLogo } from './OptimizelyLogo';
import { Dialog } from '@zoopla-eng/zds-components/dialog';
import {
    fixedButtonWithIconStyle,
    forcedStyle,
    tableDataStyle,
    tableHeaderDataStyle,
    tableHeaderStyle,
    containerStyle,
    styledTableStyle,
    tableRowStyle
} from './DevToggle.styles.css';

type Entry = Record<string, unknown> & {
    key: string;
    variations?: Record<string, unknown>[];
};
type onForceHandler = (urlParam: [string, string]) => void;

const getHeadings = (arrayOfObjects: Record<string, unknown>[]) => {
    return arrayOfObjects.reduce(
        (acc, obj) => {
            const k = Object.keys(obj);
            k.forEach((v) => {
                if (acc.indexOf(v) === -1) acc.push(v);
            });
            return acc;
        },
        ['key', 'session_state']
    );
};

const ForceDropdown: FC<
    React.PropsWithChildren<{
        entry: Entry;
        entryState: unknown;
        onForce: onForceHandler;
    }>
> = ({ entry, entryState, onForce }) => {
    const isFlag = typeof entryState === 'boolean';
    const printState = JSON.stringify(entryState);
    const variants = isFlag
        ? ['true', 'false', 'cancel']
        : [
              ...(entry?.variations?.map(({ key }) => key as string) ?? []),
              'cancel'
          ];
    const onChange = (e: ChangeEvent<HTMLSelectElement>) => {
        const val = e.target.value;
        const isCancel = val === 'cancel';
        const newVal = isCancel ? (isFlag ? 'true' : entryState) : val;
        const urlParam = `${isCancel ? 'cancel_' : ''}${
            isFlag ? 'force_feature_flag_' : 'force_test_variation_'
        }${entry.key}`;
        onForce([urlParam, newVal as string]);
    };
    return (
        <select
            name={`override_${entry.key}`}
            onChange={onChange}
            defaultValue={entryState as string}
        >
            <option value={''} disabled>
                current state: {printState}
            </option>
            {variants.map((v) => (
                <option key={v} value={v}>
                    {v}
                </option>
            ))}
        </select>
    );
};

const ForceExperimentDropdown: FC<
    React.PropsWithChildren<{
        entry: Entry;
        onForce: onForceHandler;
    }>
> = ({ entry, onForce }) => {
    const [isLoading, entryState] = useLazyOptimizelyExperiment(entry.key, {
        disableTracking: true
    });
    if (isLoading) return null;
    return (
        <ForceDropdown
            entry={entry}
            entryState={entryState}
            onForce={onForce}
        />
    );
};

const ForceFlagDropdown: FC<
    React.PropsWithChildren<{
        entry: Entry;
        onForce: onForceHandler;
    }>
> = ({ entry, onForce }) => {
    const [isLoading, isFlagEnabled] = useLazyOptimizelyFeatureFlag(entry.key, {
        disableTracking: true
    });
    if (isLoading) return null;
    return (
        <ForceDropdown
            entry={entry}
            entryState={isFlagEnabled}
            onForce={onForce}
        />
    );
};

type OptimizelyTestType = 'experiments' | 'flags';

const TableRow: FC<
    React.PropsWithChildren<{
        idx: number;
        entry: Entry;
        forcedKeys?: string[];
        headings: string[];
        onForce: onForceHandler;
        type: OptimizelyTestType;
    }>
> = ({ entry, forcedKeys, headings, onForce, type, idx = 0 }) => {
    const isForced = forcedKeys?.includes(entry.key);
    return (
        <tr
            className={
                isForced ? forcedStyle : tableRowStyle({ even: idx % 2 === 0 })
            }
        >
            {headings.map((k: string) => (
                <td className={tableDataStyle} key={entry['id'] + k}>
                    {k === 'session_state' ? (
                        type === 'experiments' ? (
                            <ForceExperimentDropdown
                                entry={entry}
                                onForce={onForce}
                            />
                        ) : (
                            <ForceFlagDropdown
                                entry={entry}
                                onForce={onForce}
                            />
                        )
                    ) : (
                        JSON.stringify(entry[k])
                    )}
                </td>
            ))}
        </tr>
    );
};

const TableView: FC<
    React.PropsWithChildren<{
        data: Entry[];
        forcedKeys?: string[];
        title: OptimizelyTestType;
        onForce: onForceHandler;
    }>
> = ({ data, forcedKeys, title, onForce }) => {
    const headings = getHeadings(data);
    return (
        <table className={styledTableStyle}>
            <thead>
                <tr>
                    <td
                        className={tableHeaderDataStyle}
                        colSpan={headings.length}
                    >
                        {title}
                    </td>
                </tr>
                <tr>
                    {headings.map((key) => (
                        <th className={tableHeaderStyle} key={key}>
                            {key}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {data.map((entry, idx) => (
                    <TableRow
                        idx={idx}
                        key={entry['id'] as string}
                        type={title}
                        entry={entry}
                        onForce={onForce}
                        headings={headings}
                        forcedKeys={forcedKeys}
                    />
                ))}
            </tbody>
        </table>
    );
};

type OptimizelyDevToggles = {
    container?: RefObject<HTMLElement>;
};

const OptimizelyDevToggles = ({ container }: OptimizelyDevToggles) => {
    const [dataFile, setDataFile] = useState<{
        experiments: Entry[];
        featureFlags: Entry[];
    }>();

    const userSetOverrides = useUserSetOptimizelyOverrides();

    useEffect(() => {
        async function fetchDataFile() {
            try {
                const res = await fetch(
                    `${process.env['NEXT_PUBLIC_OPTIMIZELY_URL']}/datafiles/${process.env['NEXT_PUBLIC_OPTIMIZELY_KEY']}.json`
                );
                if (res.ok) {
                    const dataFile = await res.json();
                    setDataFile(dataFile);
                }
            } catch (e) {
                // do nothing
            }
        }
        fetchDataFile();
    }, []);

    if (!dataFile) return null;

    const onForce: onForceHandler = ([param, val]) => {
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.set(param, val);
        window.location.search = urlParams.toString();
    };

    const forcedFlags = Object.keys(userSetOverrides?.features || {});
    const forcedExperiments = Object.keys(userSetOverrides?.experiments || {});
    const forcedKeys = [...forcedFlags, ...forcedExperiments];

    return (
        <aside aria-label="optimizely developer tools">
            <Dialog
                trigger={
                    <div className={fixedButtonWithIconStyle}>
                        <IconButton label="optimizely" category="secondary">
                            {OptimizelyLogo}
                        </IconButton>
                    </div>
                }
                label="Optimizely tool"
                container={container?.current}
                variant="fullScreen"
            >
                <div className={containerStyle}>
                    <TableView
                        title="experiments"
                        data={dataFile.experiments}
                        forcedKeys={forcedKeys}
                        onForce={onForce}
                    />
                    <TableView
                        title="flags"
                        data={dataFile.featureFlags}
                        forcedKeys={forcedKeys}
                        onForce={onForce}
                    />
                </div>
            </Dialog>
        </aside>
    );
};

export default OptimizelyDevToggles;
